import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { toast } from "sonner";
import PageHeader from "../components/PageHeader";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Plus, Trash2, Package } from "lucide-react";

export default function Products() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", notes: "" });

  const load = async () => {
    setLoading(true);
    const r = await api.get("/products");
    setItems(r.data);
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/products", { name: form.name, notes: form.notes });
      toast.success("Product added");
      setOpen(false);
      setForm({ name: "", notes: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const del = async (id) => {
    if (!window.confirm("Move this product to the Recycle Bin? You can restore within 45 days.")) return;
    await api.delete(`/products/${id}`);
    toast.success("Moved to Recycle Bin");
    load();
  };

  return (
    <div data-testid="products-page">
      <PageHeader
        title="Products"
        subtitle="Master list of diaper brands — used to autocomplete orders"
        action={
          <button
            onClick={() => setOpen(true)}
            data-testid="add-product-btn"
            className="btn-primary flex items-center gap-2"
          >
            <Plus className="h-5 w-5" /> Add Product
          </button>
        }
      />

      <div className="card-surface overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-ink-soft">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-12 text-center">
            <div className="mx-auto h-12 w-12 rounded-xl bg-forest-soft text-forest grid place-items-center mb-3">
              <Package className="h-6 w-6" />
            </div>
            <div className="font-outfit font-semibold text-ink">No products yet</div>
            <div className="text-ink-soft text-sm font-figtree">Add your diaper brands here — you'll be able to pick them from a dropdown when creating orders.</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-sand/60">
                <tr>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Product / Brand</th>
                  <th className="text-left font-figtree font-semibold text-sm text-ink-soft px-6 py-4">Notes</th>
                  <th className="text-right font-figtree font-semibold text-sm text-ink-soft px-6 py-4"></th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.id} className="border-t border-line hover:bg-bone/60">
                    <td className="px-6 py-4 font-figtree font-medium text-ink">
                      <div className="flex items-center gap-3">
                        <div className="h-8 w-8 rounded-lg bg-forest-soft text-forest grid place-items-center flex-shrink-0">
                          <Package className="h-4 w-4" />
                        </div>
                        {it.name}
                      </div>
                    </td>
                    <td className="px-6 py-4 font-figtree text-ink-soft text-sm">{it.notes || "—"}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => del(it.id)} className="p-2 rounded-lg text-ink-soft hover:bg-red-50 hover:text-red-700 transition-colors duration-200">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-outfit">Add Product</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4 mt-2">
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Brand name</label>
              <input
                data-testid="product-name-input"
                required value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="e.g. Softy Care"
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <div>
              <label className="text-sm font-figtree font-medium text-ink block mb-2">Notes (optional)</label>
              <input
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="e.g. Premium range"
                className="w-full h-12 px-4 rounded-lg border border-line outline-none focus:ring-2 focus:ring-forest"
              />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setOpen(false)} className="px-4 py-2 rounded-lg border border-line font-figtree">Cancel</button>
              <button data-testid="product-submit-btn" type="submit" className="btn-primary">Save</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
